import { handleClientSideErrors } from "./auth-error-handler";
import { BackendResponseError } from "../error-types";
import { dialogFallback, loginRequest, publicClientApp } from "./fallbackauthdialog";
import { CUSTOM_EVENTS, LOGGED_IN, LOG_IN_STATUS, LOG_IN_METHODS, buildAPIUrl, showMessage } from "../utils";

import { API_END_POINTS } from "../api-paths";

import { UserLoginInformation } from "./auth-utils";

interface NotifyUserAuthSuccessParams {
  user: {
    name: string;
    email: string;
    userProfilePhoto: string;
  };
  logInMethod?: string;
}

export async function logInUser() {
  getUserToken(createUser);
}

export function logOutUser({ user, signInMethod }) {
  notifyUserLogOutSuccess({ user, signInMethod });
}

export function notifyUserLogInSuccess({ user, logInMethod }: NotifyUserAuthSuccessParams) {
  const customEvent = new CustomEvent(CUSTOM_EVENTS.USER_LOG_IN, {
    detail: {
      isAuthenticated: true,
      signInMethod: logInMethod,
      ...user,
    },
  });
  window.dispatchEvent(customEvent);
}

export async function notifyUserLogOutSuccess({ user, signInMethod }) {
  const customEvent = new CustomEvent(CUSTOM_EVENTS.USER_LOG_OUT, {
    detail: {
      isAuthenticated: false,
      name: "",
      email: "",
      signInMethod: signInMethod,
      signedOutAccount: user.email,
    },
  });
  window.dispatchEvent(customEvent);
}

export function NotifyFailure({ msg, todo = null, sheetName = null, address = null }) {
  showMessage({ msg, todo, sheetName, address });
}

async function createUser(userToken: string, logInMethod: string) {
  try {
    const url = buildAPIUrl(API_END_POINTS.USER);

    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: "Bearer " + userToken },
      cache: "no-cache",
    });

    if (!response.ok) {
      const error = await response.json();
      const msg = error.message;
      throw new BackendResponseError(msg);
    }
    const user = await response.json();
    notifyUserLogInSuccess({ user, logInMethod });
  } catch (error) {
    let todo: string = null;
    const msg = error.message;

    if (error instanceof BackendResponseError) todo = error.todo;
    NotifyFailure({ msg, todo });
  }
}

async function getUserToken(processToken) {
  try {
    // SSO
    let userTokenEncoded = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: true,
    });
    processToken(userTokenEncoded, LOG_IN_METHODS.SSO);
  } catch (error) {
    // MSAL FallbackAuth
    if (handleClientSideErrors(error)) {
      dialogFallback(processToken);
    }
  }
}

export async function getUserTokenIfSignedIn() {
  const logInMethod = UserLoginInformation.getLogInMethod();
  let userToken: string = null;

  try {
    // Try silent token acquisition first
    if (logInMethod === LOG_IN_METHODS.SSO) {
      userToken = await OfficeRuntime.auth.getAccessToken();
    }
    if (logInMethod === LOG_IN_METHODS.MSAL) {
      userToken = (await publicClientApp.acquireTokenSilent(loginRequest)).accessToken;
    }
  } catch (error) {
    // If silent acquisition fails, retry with prompts allowed
    if (logInMethod === LOG_IN_METHODS.SSO) {
      userToken = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true });
    }
    if (logInMethod === LOG_IN_METHODS.MSAL) {
      userToken = (await publicClientApp.acquireTokenPopup(loginRequest)).accessToken;
    }
  }
  return userToken;
}

export function isUserSignedIn(): boolean {
  const logInStatus = UserLoginInformation.getLogInStatus();
  const loggedIn = UserLoginInformation.getLoggedIn();
  return logInStatus === LOG_IN_STATUS.SUCCESS && loggedIn === LOGGED_IN.YES ? true : false;
}

export async function getUserIfSignedIn() {
  const user = UserLoginInformation.getUser();
  const logInMethod = UserLoginInformation.getLogInMethod();
  notifyUserLogInSuccess({ user, logInMethod });
}
